// Ad Studio content model. Creative sets move Draft -> In review -> Approved,
// and each set carries the brief Carli generated plus the concepts, copy, and
// per-placement adaptations produced from it.

export type CreativeStatus = 'Draft' | 'In review' | 'Approved' | 'Past'

export type FormatId = '1:1' | '4:5' | '9:16' | '1.91:1' | '16:9'

export const formats: { id: FormatId; label: string; ratio: number; usage: string }[] = [
  { id: '1:1', label: 'Square', ratio: 1, usage: 'Feed, marketplace' },
  { id: '4:5', label: 'Portrait', ratio: 4 / 5, usage: 'Feed, best mobile fill' },
  { id: '9:16', label: 'Vertical', ratio: 9 / 16, usage: 'Stories, Reels' },
  { id: '1.91:1', label: 'Landscape', ratio: 1.91, usage: 'Link, discovery' },
  { id: '16:9', label: 'Wide', ratio: 16 / 9, usage: 'Video, in-stream' },
]

export type CreativeMode = 'Static' | 'Carousel' | 'Video'

export type CreativeBrief = {
  inventory: string
  objective: string
  audience: string
  offer: string
  landingPage: string
  angle: string
  channel: string
  assetType: string
  restrictions: string[]
}

export type Concept = {
  id: string
  name: string
  note: string
  image: string
  mode: CreativeMode
}

export type CopyVariation = {
  id: string
  headline: string
  body: string
  cta: string
  note: string
}

export type CreativeVersion = {
  id: string
  label: string
  author: string
  when: string
  summary: string
}

export type CreativeComment = {
  id: string
  author: string
  role: string
  when: string
  body: string
  resolved: boolean
}

export type AdaptationState = 'Approved' | 'Ready' | 'Generating' | 'Missing'

export type Placement = {
  channel: string
  logo?: string
  formats: Partial<Record<FormatId, AdaptationState>>
}

export type CreativeSet = {
  id: string
  name: string
  brand: string
  status: CreativeStatus
  updated: string
  owner: string
  assets: number
  cover: string
  brief: CreativeBrief
  concepts: Concept[]
  copy: CopyVariation[]
  versions: CreativeVersion[]
  comments: CreativeComment[]
  placements: Placement[]
  /** Populated once a set has stopped running. */
  ranWindow?: string
  result?: string
}

const manymoonsLogo = 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg'
const metaLogo = 'https://cdn.magicpatterns.com/uploads/3EsJsRbrqwgztzCzWf85MT/meta.svg'
const vibeLogo = 'https://cdn.magicpatterns.com/uploads/4QsPQBQikeUopo55GPTpMC/vibe.svg'

const studioShot = 'https://cdn.magicpatterns.com/patterns/generated-images/31bb4b25-d130-42b6-bfa5-07ca1e49771e.jpg'
const lifestyleShot = 'https://cdn.magicpatterns.com/patterns/generated-images/06bc2d99-4d1e-4cf7-b1c7-e1b4af361b80.jpg'
const flatlayShot = 'https://cdn.magicpatterns.com/patterns/generated-images/5814b8ff-8c0e-49bc-a31b-c07db853796a.jpg'
const detailShot = 'https://cdn.magicpatterns.com/patterns/generated-images/8243141d-978d-444a-b752-c6a7395b94b0.jpg'

export const creativeSets: CreativeSet[] = [
  {
    id: 'cs-1',
    name: 'Chelsea Boot — Imperfects',
    brand: 'Nisolo',
    status: 'Draft',
    updated: '12 minutes ago',
    owner: 'Carli',
    assets: 9,
    cover: studioShot,
    brief: {
      inventory: '412 units · 472 Imperfects · Chelsea Boot',
      objective: 'Accelerate sell-through on slow-moving B-grade footwear',
      audience: 'Repeat secondary-market buyers, 28–44, US and Canada',
      offer: '45% off MSRP, within the 70% public pricing limit',
      landingPage: 'manymoons.com/nisolo/imperfects',
      angle: 'Value-led — same construction, honest flaw',
      channel: 'Meta, Instagram, Vibe',
      assetType: 'Static and carousel',
      restrictions: ['Brand name allowed in ad copy', 'Supplied product imagery permitted', 'No campaign imagery or logos'],
    },
    concepts: [
      { id: 'cn-1', name: 'Studio honest', note: 'Product on neutral, flaw called out in copy not image', image: studioShot, mode: 'Static' },
      { id: 'cn-2', name: 'Worn-in', note: 'Lifestyle crop, golden hour, emphasis on wearability', image: lifestyleShot, mode: 'Static' },
      { id: 'cn-3', name: 'Paired value', note: 'Flat lay with belt and scarf, bundle framing', image: flatlayShot, mode: 'Carousel' },
      { id: 'cn-4', name: 'Craft macro', note: 'Stitch detail, leads with construction quality', image: detailShot, mode: 'Video' },
    ],
    copy: [
      {
        id: 'cp-1',
        headline: 'The boot you know, 45% off',
        body: 'Small imperfections. Same leather, same last, same resole-ready construction.',
        cta: 'Shop imperfects',
        note: 'Leads with price. Strongest for cold traffic.',
      },
      {
        id: 'cp-2',
        headline: 'Missed a quality check by millimeters',
        body: 'Rather than write them off, we priced them to be worn. Limited pairs, no restock.',
        cta: 'See what is left',
        note: 'Leads with story. Better for warm retargeting.',
      },
      {
        id: 'cp-3',
        headline: '412 pairs. One run.',
        body: 'Imperfect stock is never restocked. When a size goes, it is gone.',
        cta: 'Check your size',
        note: 'Scarcity framing. Use late in the flight.',
      },
    ],
    versions: [
      { id: 'v-3', label: 'v3', author: 'Carli', when: '12 min ago', summary: 'Regenerated square and vertical from studio concept' },
      { id: 'v-2', label: 'v2', author: 'Maya Chen', when: '2 hours ago', summary: 'Tightened headline, swapped CTA to "Shop imperfects"' },
      { id: 'v-1', label: 'v1', author: 'Carli', when: 'Yesterday', summary: 'Initial set generated from the 472 Imperfects brief' },
    ],
    comments: [
      { id: 'cm-1', author: 'Carolyn Reid', role: 'Brand', when: '1 hour ago', body: 'Can we avoid the word "defect" anywhere in copy? "Imperfect" only.', resolved: false },
      { id: 'cm-2', author: 'Maya Chen', role: 'Demand', when: '3 hours ago', body: 'Vertical crop is cutting the sole. Needs more headroom at the bottom.', resolved: false },
      { id: 'cm-3', author: 'Orr Levi', role: 'Growth', when: 'Yesterday', body: 'Approved the value-led angle for the first flight.', resolved: true },
    ],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Ready', '9:16': 'Generating', '1.91:1': 'Ready', '16:9': 'Missing' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Ready', '4:5': 'Approved', '9:16': 'Ready', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Google', formats: { '1:1': 'Ready', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Ready', '16:9': 'Generating' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Ready', '4:5': 'Ready', '9:16': 'Approved', '1.91:1': 'Missing', '16:9': 'Ready' } },
    ],
  },
  {
    id: 'cs-2',
    name: 'Summer capsule dresses',
    brand: 'Eddy',
    status: 'In review',
    updated: '2 hours ago',
    owner: 'Maya Chen',
    assets: 12,
    cover: lifestyleShot,
    brief: {
      inventory: '187 units · Spring Collection 2026 · Dresses',
      objective: 'Expand reach beyond the two channels currently live',
      audience: 'Warm audience from prior Eddy purchasers, US',
      offer: '40% off MSRP with free returns',
      landingPage: 'manymoons.com/eddy/summer-capsule',
      angle: 'Seasonal urgency — end of run sizing',
      channel: 'Meta, Instagram, Google',
      assetType: 'Carousel',
      restrictions: ['No paid shopping feeds', 'Brand name allowed in ad copy'],
    },
    concepts: [
      { id: 'cn-5', name: 'Editorial grid', note: 'Four looks, single model, consistent backdrop', image: lifestyleShot, mode: 'Carousel' },
      { id: 'cn-6', name: 'Size run', note: 'Remaining sizes shown as a visual inventory', image: flatlayShot, mode: 'Static' },
    ],
    copy: [
      { id: 'cp-4', headline: 'Last of the summer run', body: 'Four styles, limited sizing, 40% off through the season.', cta: 'Shop the capsule', note: 'Balanced urgency and value.' },
      { id: 'cp-5', headline: 'Sizes are thinning out', body: 'What is left of the spring capsule, priced to move before fall.', cta: 'See remaining sizes', note: 'Scarcity leaning.' },
    ],
    versions: [
      { id: 'v-4', label: 'v2', author: 'Maya Chen', when: '2 hours ago', summary: 'Sent to Eddy for brand review' },
      { id: 'v-5', label: 'v1', author: 'Carli', when: '2 days ago', summary: 'Generated from the summer capsule opportunity' },
    ],
    comments: [
      { id: 'cm-4', author: 'Carli', role: 'Assistant', when: '2 hours ago', body: 'Google feed placements excluded per Eddy permissions. Search and display only.', resolved: true },
    ],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Ready', '1.91:1': 'Ready', '16:9': 'Missing' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Google', formats: { '1:1': 'Ready', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Ready', '16:9': 'Ready' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Generating', '4:5': 'Generating', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
    ],
  },
  {
    id: 'cs-3',
    name: 'Oxford — Damages bundle',
    brand: 'Nisolo',
    status: 'Draft',
    updated: 'Yesterday',
    owner: 'Carli',
    assets: 4,
    cover: flatlayShot,
    brief: {
      inventory: '268 units · 473 Damages · Oxford',
      objective: 'Lift perceived value on C-grade units through pairing',
      audience: 'Value-driven buyers, discreet channels only',
      offer: '70% off MSRP, bundle pricing',
      landingPage: 'manymoons.com/nisolo/paired',
      angle: 'Paired value — boot plus belt',
      channel: 'Vibe',
      assetType: 'Static',
      restrictions: ['Discreet channels only', 'No brand name in ad copy', 'No campaign imagery or logos'],
    },
    concepts: [
      { id: 'cn-7', name: 'Pair it', note: 'Flat lay pairing, no brand marks visible', image: flatlayShot, mode: 'Static' },
    ],
    copy: [
      { id: 'cp-6', headline: 'Two pieces, one price', body: 'Leather goods with honest wear, sold as a set.', cta: 'See the set', note: 'Brand-free per restriction.' },
    ],
    versions: [{ id: 'v-6', label: 'v1', author: 'Carli', when: 'Yesterday', summary: 'Generated from the Oxford damages opportunity' }],
    comments: [],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Google', formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Ready', '4:5': 'Ready', '9:16': 'Generating', '1.91:1': 'Missing', '16:9': 'Missing' } },
    ],
  },
  {
    id: 'cs-4',
    name: 'Partnership push — August',
    brand: 'Eddy',
    status: 'Approved',
    updated: 'Aug 17',
    owner: 'Orr Levi',
    assets: 15,
    cover: detailShot,
    brief: {
      inventory: 'Brand-level · Eddy partnership announcement',
      objective: 'Sustain monthly partnership awareness during phase one',
      audience: 'Eddy owned audience plus lookalike expansion',
      offer: 'No discount — awareness only',
      landingPage: 'manymoons.com/eddy',
      angle: 'Partnership story',
      channel: 'Meta, Instagram, Vibe',
      assetType: 'Static and video',
      restrictions: ['Brand name and logos approved for this set'],
    },
    concepts: [{ id: 'cn-8', name: 'Craft macro', note: 'Construction detail as the hero', image: detailShot, mode: 'Video' }],
    copy: [
      { id: 'cp-7', headline: 'Eddy, now on Manymoons', body: 'A new home for the pieces that deserve a second run.', cta: 'Explore the collection', note: 'Approved by brand.' },
    ],
    versions: [{ id: 'v-7', label: 'v4', author: 'Orr Levi', when: 'Aug 17', summary: 'Approved and locked for the August flight' }],
    comments: [{ id: 'cm-5', author: 'Orr Levi', role: 'Growth', when: 'Aug 17', body: 'Locked. Do not regenerate without a new brief.', resolved: true }],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Approved', '16:9': 'Approved' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Ready', '16:9': 'Ready' } },
      { channel: 'Google', formats: { '1:1': 'Approved', '4:5': 'Ready', '9:16': 'Missing', '1.91:1': 'Approved', '16:9': 'Approved' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Ready', '16:9': 'Approved' } },
    ],
  },
  {
    id: 'cs-5',
    name: 'Spring clearance — Dresses',
    brand: 'Eddy',
    status: 'Past',
    updated: 'Ended Jul 30',
    owner: 'Maya Chen',
    assets: 11,
    cover: lifestyleShot,
    ranWindow: 'Jun 2 – Jul 30, 2026',
    result: '$41,200 recovered · 3.1x ROAS',
    brief: {
      inventory: '640 units · Spring Collection 2026 · Dresses',
      objective: 'Clear remaining spring sizing before the summer flight',
      audience: 'Cold prospecting plus site retargeting, US',
      offer: '50% off MSRP',
      landingPage: 'manymoons.com/eddy/spring',
      angle: 'End of season clearance',
      channel: 'Meta, Instagram',
      assetType: 'Static and carousel',
      restrictions: ['Brand name allowed in ad copy'],
    },
    concepts: [{ id: 'cn-9', name: 'Editorial grid', note: 'Four looks on a consistent backdrop', image: lifestyleShot, mode: 'Carousel' }],
    copy: [
      { id: 'cp-8', headline: 'Spring, half off', body: 'The last of the spring capsule, priced to clear.', cta: 'Shop spring', note: 'Best performer of the flight.' },
    ],
    versions: [{ id: 'v-8', label: 'v3', author: 'Maya Chen', when: 'Jul 30', summary: 'Flight ended, set archived' }],
    comments: [{ id: 'cm-6', author: 'Maya Chen', role: 'Demand', when: 'Jul 30', body: 'Strong first three weeks, fatigued after that. Reuse the grid concept.', resolved: true }],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Approved', '16:9': 'Missing' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Google', formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
    ],
  },
  {
    id: 'cs-6',
    name: 'Loafer restock — Privé',
    brand: 'Nisolo',
    status: 'Past',
    updated: 'Ended Jun 14',
    owner: 'Carli',
    assets: 6,
    cover: detailShot,
    ranWindow: 'May 1 – Jun 14, 2026',
    result: '$12,750 recovered · 1.4x ROAS',
    brief: {
      inventory: '190 units · 472 Imperfects · Loafer',
      objective: 'Test discreet-channel demand on a single silhouette',
      audience: 'Privé members only',
      offer: '55% off MSRP',
      landingPage: 'manymoons.com/prive',
      angle: 'Members-first access',
      channel: 'Vibe',
      assetType: 'Static',
      restrictions: ['No brand name in ad copy', 'Discreet channels only'],
    },
    concepts: [{ id: 'cn-10', name: 'Craft macro', note: 'Detail crop, no brand marks visible', image: detailShot, mode: 'Static' }],
    copy: [
      { id: 'cp-9', headline: 'Members get first look', body: 'A small run of leather loafers, released quietly.', cta: 'View the run', note: 'Underperformed against forecast.' },
    ],
    versions: [{ id: 'v-9', label: 'v2', author: 'Carli', when: 'Jun 14', summary: 'Flight ended below forecast, set archived' }],
    comments: [{ id: 'cm-7', author: 'Orr Levi', role: 'Growth', when: 'Jun 14', body: 'Discreet-only limited reach too far. Pair with Manymoons next time.', resolved: true }],
    placements: [
      { channel: 'Meta', logo: metaLogo, formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Instagram', logo: metaLogo, formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Google', formats: { '1:1': 'Missing', '4:5': 'Missing', '9:16': 'Missing', '1.91:1': 'Missing', '16:9': 'Missing' } },
      { channel: 'Vibe', logo: vibeLogo, formats: { '1:1': 'Approved', '4:5': 'Approved', '9:16': 'Approved', '1.91:1': 'Missing', '16:9': 'Missing' } },
    ],
  },
]

export const manymoonsMark = manymoonsLogo
